import React, { useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { AzureDevOpsService } from '../services/AzureDevOpsService';
import { PublicClientApplication } from '@azure/msal-browser';
import { RND_PLANNING_PROJECT } from '../Common/Constants';
import { Input, Button } from 'antd';

interface WorkInProgressPageProps {
  userName: string;
  setUserName: React.Dispatch<React.SetStateAction<string>>;
}

const WorkInProgressPage: React.FC<WorkInProgressPageProps> = ({ userName, setUserName }) => {
  const { instance, accounts } = useMsal();
  const [userProfile, setUserProfile] = useState<any>(null);
  const [azureBoardsData, setAzureBoardsData] = useState<any>(null);
  const [wiql, setWiql] = useState<string>("Select [System.Id] , [System.Title], [System.State], [Custom.EndIteration], [Custom.StartIteration] From WorkItems Where [Custom.EndIteration] <> '' AND [State] <> 'Closed' and [System.TeamProject] = @project order by [Custom.PlanningPriority] asc, [Custom.StartIteration] asc, [System.CreatedDate] asc");
  const [tickets, setTickets] = useState<any>(null);
  const [workItemUrl, setWorkItemUrl] = useState<string>("");
  const [workItem, setWorkItem] = useState<any>(null);

  const fetchUserProfile = async () => {
    if (accounts.length > 0) {
      try {
        const devOpsService = new AzureDevOpsService(instance as PublicClientApplication, accounts[0]);
        // Fetch user profile from Azure DevOps (connectionData endpoint)
        const url = `https://dev.azure.com/sensio/_apis/connectionData?connectOptions=1`;
        const accessToken = await devOpsService.getAccessToken();
        if (!accessToken) {
          throw new Error('No access token available');
        }
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Accept': 'application/json',
          },
        });
        if (!response.ok) {
          throw new Error(`Error fetching Azure DevOps user profile: ${response.statusText}`);
        }
        const profileData = await response.json();
        setUserProfile(profileData.authenticatedUser.providerDisplayName);
        setUserName(profileData.authenticatedUser.providerDisplayName);
        console.log("Azure DevOps User Profile:", profileData);
        if (
          profileData &&
          profileData.authenticatedUser &&
          profileData.authenticatedUser.providerDisplayName
        ) {
          console.log("Authenticated user's providerDisplayName:", profileData.authenticatedUser.providerDisplayName);
        }
      } catch (error) {
        console.error("Error fetching Azure DevOps user profile:", error);
      }
    } else {
      console.warn("No accounts available. Please log in.");
    }
  };

  const fetchAzureBoardsData = async () => {
    if (accounts.length > 0) {
      try {
        // Cast instance to PublicClientApplication to satisfy the constructor
        const devOpsService = new AzureDevOpsService(instance as PublicClientApplication, accounts[0]);
        // Replace 'sensio' with your actual organization name if needed
        const projectsData = await devOpsService.fetchProjects('sensio');
        setAzureBoardsData(projectsData);
        console.log("Azure DevOps Projects Data:", projectsData);
      } catch (error) {
        console.error("Error fetching Azure DevOps projects data:", error);
      }
    } else {
      console.warn("No accounts available. Please log in.");
    }
  };

  const fetchTickets = async () => {
    if (accounts.length > 0) {
      try {
        const devOpsService = new AzureDevOpsService(instance as PublicClientApplication, accounts[0]);
        // Example: organization = 'sensio', project = 'YOUR_PROJECT_NAME'
        const organization = 'sensio';
        const project = RND_PLANNING_PROJECT; // TODO: Make this dynamic if needed
        const result = await devOpsService.fetchTicketsByQuery(organization, project, wiql);
        setTickets(result);
      } catch (error) {
        console.error("Error fetching tickets:", error);
      }
    } else {
      console.warn("No accounts available. Please log in.");
    }
  };

  const fetchWorkItem = async () => {
    if (accounts.length > 0 && workItemUrl) {
      try {
        const devOpsService = new AzureDevOpsService(instance as PublicClientApplication, accounts[0]);
        const result = await devOpsService.fetchWorkItemByUrl(workItemUrl);
        setWorkItem(result);
      } catch (error) {
        console.error("Error fetching work item:", error);
      }
    } else {
      console.warn("No accounts available or URL missing. Please log in and provide a URL.");
    }
  };

  return (
    <div>
      <h1>Work in Progress</h1>
      <p>This page is under construction. Please check back later!</p>
      <button onClick={fetchUserProfile}>Fetch User Profile</button>
      {userProfile && (
        <div>
          <h2>User Profile</h2>
          <pre>{JSON.stringify(userProfile, null, 2)}</pre>
        </div>
      )}
      <button onClick={fetchAzureBoardsData}>Fetch Azure DevOps Projects</button>
      {/* WIQL Query Input and Fetch */}
      <div style={{ marginTop: 20 }}>
        <h2>Fetch Tickets by WIQL</h2>
        <Input.TextArea
          value={wiql}
          onChange={e => setWiql(e.target.value)}
          rows={6}
          placeholder="Type your WIQL query here..."
          style={{ fontFamily: 'monospace', fontSize: 14, width: 600 }}
        />
        <br />
        <Button onClick={fetchTickets}>Fetch Tickets</Button>
        {tickets && (
          <div>
            <h3>Tickets Result</h3>
            {Array.isArray(tickets?.workItems) && tickets.workItems.length > 0 ? (
              <ul>
              {tickets.workItems.map((item: any) => (
                <li key={item.id}>
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {item.url}
                </a>
                </li>
              ))}
              </ul>
            ) : (
              <pre>{JSON.stringify(tickets, null, 2)}</pre>
            )}
          </div>
        )}
      </div>
      {/* Work Item by URL Input and Fetch */}
      <div style={{ marginTop: 20 }}>
        <h2>Fetch Work Item by URL</h2>
        <Input
          value={workItemUrl}
          onChange={e => setWorkItemUrl(e.target.value)}
          placeholder="Paste Azure DevOps work item API URL here..."
          style={{ width: 400, fontFamily: 'monospace', fontSize: 14 }}
        />
        <Button onClick={fetchWorkItem} style={{ marginLeft: 8 }}>Fetch Work Item</Button>
        {workItem && (
          <div>
            <h3>Work Item Result</h3>
            <pre>{JSON.stringify(workItem, null, 2)}</pre>
          </div>
        )}
      </div>
      {azureBoardsData && (
        <div>
          <h2>Azure DevOps Projects</h2>
          <pre>{JSON.stringify(azureBoardsData, null, 2)}</pre>
          {azureBoardsData.value && Array.isArray(azureBoardsData.value) && (
            <ul>
              {azureBoardsData.value.map((project: any) => (
                <li key={project.id}><strong>{project.name}</strong> - {project.state}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};


export default WorkInProgressPage;
